import React, {useState, useMemo, useCallback, useEffect, useRef} from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { withStyles, Tooltip, IconButton, Typography } from '@material-ui/core';
import { hasPermission, createHistory, hasAtLeastOnePermission } from '../../sc-utilities';
import ErasureMethodList from './ErasureMethodList';
import * as actions from "./store/erasureMethodActions";
import { FindInPage } from '@material-ui/icons';
import { CancelToken } from 'axios';

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingTop: '0.3rem',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    }
});

const ErasureMethod = props => {
    const tableRef = useRef();
    const { classes, columns, translations, permissions, activeCustomerID, onListSupportedClientTypes } = props;
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, ErasureMethodID: null, redirectPath: '' });
    const [supportedClientTypes, setSupportedClientTypes] = useState(null);

    useEffect(() => {
        const requestToken = CancelToken.source();

        const fetchSupportedClientTypes = async () => {
            let supportedClientTypes = await onListSupportedClientTypes({ ActiveCustomerID: activeCustomerID }, { CancelToken: requestToken.token });

            let formattedSupportedClientTypes = supportedClientTypes.reduce(
                (obj, item) => Object.assign(obj, { [item.value]: item.label }), {});
            setSupportedClientTypes(formattedSupportedClientTypes);
        };
        fetchSupportedClientTypes();

        return () => {
            requestToken.cancel('Operation canceled by the user.');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getDedicatedFor = useCallback(dedicatedFor => {
        switch (dedicatedFor) {
            case 'magnetic':
                return <Typography variant={'caption'}>{translations['magnetic'] || 'Magnetic'}</Typography>;
            case 'flash':
                return <Typography variant={'caption'}>{translations['flash'] || 'Flash'}</Typography>;
            case 'all':
                return <Typography variant={'caption'}>{translations['all'] || 'All'}</Typography>;
            default:
                return '';
        }
    }, [translations]);

    let visible = columns && columns['ErasureMethod'];
    const tableColumns = useMemo(() => {
        return [
            {
                title: translations["action"] || "Action", field: 'Action',
                hidden: visible ? visible['Action'] : false,
                cellStyle: { minWidth: 100, maxWidth: 100 },
                filtering: false,
                render: rowData => <>
                    {
                        hasPermission(permissions, 'erasure_method_load') ? <Tooltip title={translations["erasure_method_details"] || "Erasure method details"}>
                            <IconButton value={rowData.ErasureMethodID} aria-label={translations["erasure_method_details"] || "Erasure method details"} onClick={() => redirectHandler(`/admin/erasureMethod/edit?ErasureMethodID=${rowData.ErasureMethodID}`)}>
                                <FindInPage />
                            </IconButton>
                        </Tooltip> : ''
                    }
                </>
            },
            {
                title: translations["key"] || 'Key', field: 'Key',
                hidden: visible ? visible['Key'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Key ? rowData.Key : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["visible"] || 'Visible', field: 'Visible',
                hidden: visible ? visible['Visible'] : false,
                lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
                defaultFilter: ['0', '1'],
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Visible ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
                }
            },
            {
                title: translations["long_name"] || 'Long name', field: 'LongName',
                hidden: visible ? visible['LongName'] : false,
                filtering: true,
                width: 200,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.LongName ? rowData.LongName : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["short_name"] || 'Short name', field: 'ShortName',
                hidden: visible ? visible['ShortName'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.ShortName ? rowData.ShortName : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["valid_for"] || 'Valid for', field: 'SupportedClientTypeID',
                hidden: visible ? visible['SupportedClientTypeID'] : false,
                lookup: supportedClientTypes,
                filtering: true,
                width: 100,
                render: rowData => {
                    return rowData.SupportedClientTypes.length > 0 ? rowData.SupportedClientTypes.slice(0, 3).map((supportedClientType, index) => {
                        return <Typography key={supportedClientType.SupportedClientTypeID} variant={'caption'}>{supportedClientType.Name}{index !== 2 ? <br /> : '...'}</Typography>;
                    }) : <Typography variant={'caption'}> n/a </Typography>;
                }
            },
            {
                title: translations["dedicated_for"] || 'Dedicated for', field: 'DedicatedFor',
                hidden: visible ? visible['DedicatedFor'] : false,
                lookup: { 'magnetic': translations['magnetic'] || 'Magnetic', 'flash': translations['flash'] || 'Flash' },
                defaultFilter: ['magnetic', 'flash'],
                filtering: true,
                width: 100,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.DedicatedFor ? getDedicatedFor(rowData.DedicatedFor) : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["info_text"] || 'Info text', field: 'Description',
                hidden: visible ? visible['Description'] : false,
                filtering: true,
                width: 200,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Description ? rowData.Description : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
        ];
    }, [translations, visible, supportedClientTypes, permissions, getDedicatedFor])

    if (!hasAtLeastOnePermission(permissions, ['erasure_method_load'])) {
        tableColumns.shift();
    }

    const redirectHandler = (path, ErasureMethodID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, ErasureMethodID: ErasureMethodID, redirectPath: path })
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'erasure_method_list') && supportedClientTypes ?
                <ErasureMethodList
                    tableColumns={tableColumns}
                    redirectHandler={redirectHandler}
                    tableRef={tableRef}
                />
                :
                null}
        </div>
    );
};

const mapStateToProps = state => {
    return {
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || [],
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onListSupportedClientTypes: (data, option) => dispatch(actions.listSupportedClientTypes(data, option)),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(ErasureMethod));
